import { createContext } from "react";
import Call from "../../models/Call";
import { CallSession } from "../../models/CallSession";
import LeadList from "../../models/LeadList";
import { LeadListMode } from "../../models/LeadListMode";
import { IncomingCall } from "../../models/IncomingCall";
import { HangUpOptions, TelephonyServiceError, TelephonyServiceRef, TelephonyServiceStatus } from "./TelephonyService";
import { ContactEvent } from "../contacts/types";
import { UserCallStatus } from "../../models/CallStatisticsItem";
import { CallEvaluationOptions } from "../../models/CallSessionTypes";
import { BasicFieldReference } from "../../models/BasicField";

//  -------------------------------
//  NOTE: Avoid heavy imports here!
//  -------------------------------

//  Note that the order of the section affects the view transition.
//  To prevent bugs, do not have a value of 0.
export enum TelephonySectionType {
  Standard = 1,
  CohearingMessages = 2,
  LeadListList = 3,
  EvaluateCall = 4,
  ContactInfo = 5,
  Utilities = 6,
  NumberPad = 7,

  /**
   * Modal only.
   */
  Transfer = 8,
  ContactEvents = 9,
}

type LeadListRealtimeData = Pick<LeadList, 'predictiveDialHaltReason'>;

export enum PredictiveModeEndReason {
  /**
   * Predictive mode was manually ended by the user.
   */
  Normal = 'normal',

  /**
   * Predictive mode ended automatically due to a task, such as creating an offer.
   */
  Task = 'task'
}

export type TelephonyInternalState = {
  isCreatingCall: boolean;

  /**
   * Last error when attempting to make a call.
   */
  callError?: { reason?: string; error: any };

  /**
   * A list of call sessions.
   */
  callSessions: CallSession[];

  leadListRealtimeData: LeadListRealtimeData;

  /**
   * Whether the app has microphone access.
   */
  isMicrophonePermissionGranted?: boolean;

  /**
   * Whether to start telephony service, check devices etc.
   */
  isEnabled: boolean;

  /**
   * Whether the user is in an active call in another tab.
   */
  isBusyInAnotherTab?: boolean;

  serviceStatus: TelephonyServiceStatus;
  serviceError?: TelephonyServiceError;
  cohearing: TelephonyCohearingState;
  isStartingPredictiveMode: boolean;
  isRestartingLeadList: boolean;
  isRecreatingCall: boolean;
  userStatus: UserCallStatus;
  userStatusSince: Date;
  service: TelephonyServiceRef | null;

  /**
   * Error when attempting to start predictive mode.
   */
  predictiveModeError?: any;
}

export enum MuteReason {
  MutedByUser = 1,
  PlayingCallNotification = 2,
  NotWhispering = 3,
  CallTakeover = 4,
}

export type TelephonyState = {
  /**
   * Whether we are reserving a contact.
   */
  isReservingContact: boolean;

  /**
   * Error while reserving a contact.
   */
  contactReserveError?: any;

  /**
   * Whether the secondary widget should be visible for selecting call utilities.
   * 
   * Note that the secondary widget can still be visible if there are calls to evaluate in a separate widget.
   */
  isCallUtilitiesWidgetVisible: boolean;
  isTelephonyWidgetOpen: boolean;
  isLeadListListOpen: boolean;
  isLeadListModeModalOpen: boolean;

  /**
   * Whether everything is set up and we're ready to make a call.
   */
  isServiceReady: boolean;

  /**
   * Whether currently in the telephony view (contact view).
   */
  isView: boolean;

  /**
   * The current lead list to call contacts in.
   */
  leadList?: LeadList;

  /**
   * The mode of which contacts are being called.
   */
  leadListMode?: LeadListMode;

  /**
   * @deprecated Quickfix for LegacyEassyTelecomTelephonyService
   */
  pendingLeadListMode?: LeadListMode;

  /**
   * If set, then the microphone is muted. The value provides more information
   * on why the microphone is muted.
   */
  muteReason?: MuteReason | null;

  /**
   * Unless true, then the microphone is muted automatically.
   * 
   * This way we avoid accidentally unmuting the listener unless the listener explicitly
   * unmuted themselves.
   */
  isWhispering: boolean;
  callEvaluationOptions?: CallEvaluationOptions;
  section: TelephonySectionType;

  /**
   * Previous call session which can be resumed.
   */
  resumable?: { session: CallSession; leadList?: LeadList };

  /**
   * Optional ID of a call session which the user is currently evaluating.
   * This allows the user to select one of multiple call sessions to evaluate.
   * 
   * If null, then should default to the current session.
   */
  evaluatingCallSessionId?: string;

  isConfettiVisible: boolean;

  /**
   * See TelephonyWidgetHeader
   */
  notifyContactEventsInWidget: boolean;

  /**
   * Last reason why predictive mode was ended.
   */
  predictiveModeEndReason?: PredictiveModeEndReason;

  predictiveModeEndAt?: Date;

  /**
   * Whether the user is currently in a switch from one user to another.
   */
  isSwitchingCohearingUser?: boolean;

  isCreatingOutboundCall: boolean;
}

export type TelephonyHelpers = {

  /**
   * Hangs up the active call. Due to the usage of this method, it will not throw errors,
   * instead it returns whether the operation was successful.
   * 
   * @returns Whether the active call was hung up successfully.
   */
  hangUp: (options?: HangUpOptions) => boolean;

  /**
   * Reserves a contact, either a specific or the next one, and creates a call session.
   * The user should review the contact's info and make the call when they're ready.
   */
  reserveContact: (contact?: { leadList: LeadList; contactId: string; }) => Promise<CallSession>;

  /**
   * Returns the current call session.
   */
  getCurrentCallSession: () => CallSession | null;

  /**
   * Updates a call session.
   */
  updateCallSession: (update: Update<CallSession>, sessionIdOrIndex?: number | string) => void;

  /**
   * Deletes a call session.
   * 
   * @param sessionIdOrIndex Either a session ID or index. Omit to delete the current session.
   * 
   * @returns The deleted session.
   */
  deleteCallSession: (sessionIdOrIndex?: number | string) => void;

  /**
   * Sends a DTMF digit.
   */
  sendDigit: (digit: string) => void;

  startUserCohearing: (userId: string) => Promise<void>;
  startCallCohearing: (call: Call) => Promise<void>;
  callNow: (phoneNumber: string) => Promise<void>;

  /**
   * Calls a phone number after selecting it in the SelectPhoneNumberSection.
   */
  selectAndCallNow: (phoneNumber: string) => Promise<void>;

  /**
   * Creates a session. The number might be called now.
   */
  createSession: (session: CallSession, options?: CreateCallSessionOptions) => Promise<void>;

  /**
   * Check done before starting sequential or predictive mode.
   */
  checkCanStartLeadList: () => boolean;
  startSequentialMode: (leadList: LeadList) => Promise<boolean>

  startPredictiveMode: (leadList: LeadList) => Promise<boolean>

  endCohearing: () => void;

  /**
   * Answers an inbound or transferred call.
   */
  answerCall: (call: IncomingCall) => Promise<void>;

  /**
   * Refreshes contact events relevant to the current call session.
   */
  refreshContactEvents: (signal?: AbortSignal) => Promise<void>;

  /**
   * Calls the previous contact or number again.
   */
  recreateCall: (phoneNumberType?: 'new' | 'previous') => Promise<void>;

  /**
   * Starts the previous dial group again with the same mode as before.
   */
  restartLeadList: () => Promise<void>;

  /**
   * Opens the widget or navigates to the view, depending on preference.
   */
  open: (type?: 'widget' | 'view' | 'auto') => void;

  /**
   * Updates a call session's contact field references.
   * 
   * Automatically sets the updated contact phone number.
   */
  updateCallSessionContactFields: (references: BasicFieldReference[], sessionId: string) => void;

  /**
   * During co-hearing, takes over the call.
   */
  takeOverCall: () => void;

  /**
   * If set, then cannot call a number directly.
   */
  getCallNowDisabledReason: () => string | null;

  previousSection?: TelephonySectionType;

  /**
   * Whether the useContext component is rendered in the SecondaryTelephonyWidget.
   */
  isCallEvaluationWidgetChild: boolean;

  /**
   * Whether the useContext component is rendered in the SecondaryTelephonyWidget.
   */
  isCallUtilitiesWidgetChild: boolean;

  /**
   * List of contact events from ContactEventContextProvider which is unrelated to the current call session.
   */
  earlierContactEvents: ContactEvent[];

  anyError?: any;
}

export type TelephonyCohearingSubState = {
  error?: any;
  isBusy: boolean;
};

export type TelephonyCohearingState = {
  call?: TelephonyCohearingSubState & { call: Call; };
  user?: TelephonyCohearingSubState & {
    userId: string;
    isActive: boolean;
  };

  takeover: { isBusy?: boolean; isDone?: boolean };
};

export type SetTelephonyStateFn = (update: Update<TelephonyState>) => void;
export type SetTelephonyInternalStateFn = (update: Partial<TelephonyInternalState>) => void;
export type SetTelephonyCohearingContextStateFn = (update: Partial<TelephonyState>) => void;

export type TelephonyContextType = TelephonyState & TelephonyInternalState & TelephonyHelpers & {
  setState: SetTelephonyStateFn;
};

export type CreateCallSessionOptions = {
  replaceCurrent?: boolean;
}

export const TelephonyContext = createContext<TelephonyContextType>(null);